import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiEdit2, FiEye } from 'react-icons/fi';
import { fetchLeads } from '../api/leadService';
import { normalizeLead } from '../utils/leadModel';

function statusGroup(status) {
  if (status === 'Converted') return 'Converted';
  if (status === 'Lost' || status === 'Rejected') return 'Closed - Lost';
  if (status === 'Nurturing') return 'Nurturing';
  return 'Active';
}

function ContactsPage() {
  const navigate = useNavigate();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [activeContact, setActiveContact] = useState(null);

  useEffect(() => {
    let mounted = true;

    async function loadAllContacts() {
      setLoading(true);
      setError('');
      try {
        let page = 1;
        const combined = [];
        const limit = 100;

        while (page <= 30) {
          const res = await fetchLeads({ page, limit });
          const rows = Array.isArray(res.data) ? res.data : [];
          combined.push(...rows.map(normalizeLead));
          if (!res.pagination?.has_next_page) break;
          page += 1;
        }

        if (mounted) setContacts(combined);
      } catch (err) {
        if (mounted) setError(err?.response?.data?.message || 'Unable to load contacts.');
      } finally {
        if (mounted) setLoading(false);
      }
    }

    loadAllContacts();
    return () => {
      mounted = false;
    };
  }, []);

  const sourceOptions = useMemo(() => {
    const set = new Set();
    contacts.forEach((c) => {
      if (c.source && c.source !== '—') set.add(c.source);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [contacts]);

  const filteredContacts = useMemo(() => {
    const q = search.trim().toLowerCase();
    return contacts.filter((contact) => {
      const bySearch =
        !q
        || contact.fullName.toLowerCase().includes(q)
        || contact.email.toLowerCase().includes(q)
        || contact.companyName.toLowerCase().includes(q)
        || contact.phone.toLowerCase().includes(q);
      const byStatus = !statusFilter || contact.status === statusFilter;
      const bySource = !sourceFilter || contact.source === sourceFilter;
      return bySearch && byStatus && bySource;
    });
  }, [contacts, search, statusFilter, sourceFilter]);

  const summary = useMemo(() => {
    const total = contacts.length;
    const active = contacts.filter((c) => ['New', 'Contacted', 'Qualified', 'Nurturing'].includes(c.status)).length;
    const converted = contacts.filter((c) => c.status === 'Converted').length;
    const highPriority = contacts.filter((c) => ['High', 'Urgent'].includes(c.priority)).length;
    return { total, active, converted, highPriority };
  }, [contacts]);

  return (
    <div className="dashboard-page">
      <div className="page-title-row">
        <h2>Contacts</h2>
      </div>

      <section className="dashboard-kpis contacts-kpis">
        <article className="dashboard-kpi-card contacts-kpi-card contacts-kpi-card--total">
          <span>Total Contacts</span>
          <strong>{summary.total}</strong>
        </article>
        <article className="dashboard-kpi-card contacts-kpi-card contacts-kpi-card--active">
          <span>Active Contacts</span>
          <strong>{summary.active}</strong>
        </article>
        <article className="dashboard-kpi-card contacts-kpi-card contacts-kpi-card--converted">
          <span>Converted Contacts</span>
          <strong>{summary.converted}</strong>
        </article>
        <article className="dashboard-kpi-card contacts-kpi-card contacts-kpi-card--priority">
          <span>High Priority</span>
          <strong>{summary.highPriority}</strong>
        </article>
      </section>

      <div className="filter-bar">
        <input
          className="filter-input"
          placeholder="Search name, email, company, phone..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select
          className="filter-select"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="">All Statuses</option>
          <option value="New">New</option>
          <option value="Contacted">Contacted</option>
          <option value="Qualified">Qualified</option>
          <option value="Nurturing">Nurturing</option>
          <option value="Converted">Converted</option>
          <option value="Lost">Lost</option>
          <option value="Rejected">Rejected</option>
        </select>
        <select
          className="filter-select"
          value={sourceFilter}
          onChange={(e) => setSourceFilter(e.target.value)}
        >
          <option value="">All Sources</option>
          {sourceOptions.map((source) => (
            <option key={source} value={source}>{source}</option>
          ))}
        </select>
      </div>

      {loading ? <p>Loading contacts...</p> : null}
      {error ? <p className="error-text">{error}</p> : null}

      {!loading && !error ? (
        <div className="dashboard-panel dashboard-table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Contact</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Company</th>
                <th>Status</th>
                <th>Priority</th>
                <th>Source</th>
                <th>Last Updated</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredContacts.length ? (
                filteredContacts.map((contact) => (
                  <tr key={contact.id}>
                    <td>
                      <button type="button" className="lead-name-btn" onClick={() => setActiveContact(contact)}>
                        {contact.fullName}
                      </button>
                    </td>
                    <td>{contact.email}</td>
                    <td>{contact.phone}</td>
                    <td>{contact.companyName}</td>
                    <td>{contact.status}</td>
                    <td>{contact.priority}</td>
                    <td>{contact.source}</td>
                    <td>{contact.updatedAtLabel}</td>
                    <td>
                      <div className="table-actions">
                        <button
                          type="button"
                          className="icon-action-btn"
                          title="View contact"
                          aria-label="View contact"
                          data-tooltip="View contact"
                          onClick={() => setActiveContact(contact)}
                        >
                          <FiEye />
                        </button>
                        <button
                          type="button"
                          className="icon-action-btn"
                          title="Edit lead"
                          aria-label="Edit lead"
                          data-tooltip="Edit lead"
                          onClick={() => navigate(`/leads/${contact.id}/edit`)}
                        >
                          <FiEdit2 />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr><td colSpan="9">No contacts found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      ) : null}

      {activeContact && (
        <>
          <div className="drawer-backdrop" onClick={() => setActiveContact(null)} />
          <aside className="lead-drawer">
            <div className="drawer-header">
              <h3>{activeContact.fullName}</h3>
              <button className="modal-close" onClick={() => setActiveContact(null)} aria-label="Close">x</button>
            </div>

            <div className="drawer-section">
              <p className="drawer-section-title">Contact Details</p>
              <div className="drawer-row">
                <span className="drawer-label">Email</span>
                <span className="drawer-value">{activeContact.email}</span>
              </div>
              <div className="drawer-row">
                <span className="drawer-label">Phone</span>
                <span className="drawer-value">{activeContact.phone}</span>
              </div>
              <div className="drawer-row">
                <span className="drawer-label">Company</span>
                <span className="drawer-value">{activeContact.companyName}</span>
              </div>
              <div className="drawer-row">
                <span className="drawer-label">Source</span>
                <span className="drawer-value">{activeContact.source}</span>
              </div>
              <div className="drawer-row">
                <span className="drawer-label">Status Group</span>
                <span className="drawer-value">{statusGroup(activeContact.status)}</span>
              </div>
              <div className="drawer-row">
                <span className="drawer-label">Updated</span>
                <span className="drawer-value">{activeContact.updatedAtLabel}</span>
              </div>
            </div>

            <div className="drawer-actions">
              <button
                type="button"
                className="primary-btn"
                onClick={() => navigate(`/leads/${activeContact.id}/edit`)}
              >
                Edit Contact
              </button>
              <button type="button" className="ghost-btn" onClick={() => setActiveContact(null)}>
                Close
              </button>
            </div>
          </aside>
        </>
      )}
    </div>
  );
}

export default ContactsPage;
